import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MemoryService from './MemoryService';
import './MemoryDetail.css';

function MemoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [memory, setMemory] = useState(null);
  const [allMemories, setAllMemories] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loading, setLoading] = useState(true);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [password, setPassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMemories();
  }, [id]);

  const loadMemories = async () => {
    try {
      const memories = await MemoryService.loadMemories();
      setAllMemories(memories);
      
      const foundIndex = memories.findIndex(m => m.id.toString() === id);
      if (foundIndex !== -1) {
        setMemory(memories[foundIndex]);
        setCurrentIndex(foundIndex);
      }
    } catch (error) {
      console.error('Error loading memories:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToMemory = (index) => {
    if (index >= 0 && index < allMemories.length) {
      const targetMemory = allMemories[index];
      navigate(`/memory/${targetMemory.id}`, { replace: true });
    }
  };

  const handleSwipe = (direction) => {
    if (direction === 'left' && currentIndex < allMemories.length - 1) {
      // Swipe left = next photo
      navigateToMemory(currentIndex + 1);
    } else if (direction === 'right' && currentIndex > 0) {
      // Swipe right = previous photo
      navigateToMemory(currentIndex - 1);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'ArrowLeft' && currentIndex > 0) {
      navigateToMemory(currentIndex - 1);
    } else if (event.key === 'ArrowRight' && currentIndex < allMemories.length - 1) {
      navigateToMemory(currentIndex + 1);
    } else if (event.key === 'Escape') {
      navigate('/');
    }
  };

  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let endX = 0;
    let endY = 0;

    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      // Prevent default scrolling behavior during swipe
      if (Math.abs(e.touches[0].clientX - startX) > Math.abs(e.touches[0].clientY - startY)) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e) => {
      endX = e.changedTouches[0].clientX;
      endY = e.changedTouches[0].clientY;
      
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const minSwipeDistance = 50;
      
      // Only trigger swipe if horizontal movement is greater than vertical
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          handleSwipe('right'); // Swipe right = previous
        } else {
          handleSwipe('left');  // Swipe left = next
        }
      }
    };

    // Add event listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('keydown', handleKeyPress);

    // Cleanup
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [currentIndex, allMemories]);

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      await MemoryService.deleteMemory(memory.id, password);
      navigate('/', { replace: true });
    } catch (error) {
      setError(error.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="memory-detail-loading">Loading memory...</div>;
  }

  if (!memory) {
    return <div className="memory-detail-error">Memory not found</div>;
  }

  return (
    <div className="memory-detail">
      <div className="memory-detail-content">
        <img 
          src={memory.imageUrl} 
          alt={memory.title}
          className="memory-detail-image"
        />
        <div className="memory-detail-info">
          <h1 className="memory-detail-title">{memory.title}</h1>
          <p className="memory-detail-date">{memory.date}</p>
          {memory.description && (
            <p className="memory-detail-description">{memory.description}</p>
          )}
        </div>
      </div>

      {/* Navigation click areas and indicators */}
      {allMemories.length > 1 && (
        <>
          {/* Invisible click areas */}
          {currentIndex > 0 && (
            <div 
              className="nav-click-area nav-click-area-left"
              onClick={() => navigateToMemory(currentIndex - 1)}
              title="Previous photo"
            />
          )}
          {currentIndex < allMemories.length - 1 && (
            <div 
              className="nav-click-area nav-click-area-right"
              onClick={() => navigateToMemory(currentIndex + 1)}
              title="Next photo"
            />
          )}
          
          {/* Navigation indicators */}
          <div className="memory-navigation">
            <div className="memory-counter">
              {currentIndex + 1} of {allMemories.length}
            </div>
            {currentIndex > 0 && (
              <button 
                className="nav-arrow nav-arrow-left"
                onClick={() => navigateToMemory(currentIndex - 1)}
                title="Previous photo"
              >
                ‹
              </button>
            )}
            {currentIndex < allMemories.length - 1 && (
              <button 
                className="nav-arrow nav-arrow-right"
                onClick={() => navigateToMemory(currentIndex + 1)}
                title="Next photo"
              >
                ›
              </button>
            )}
          </div>
        </>
      )}

      {/* Nearly hidden delete button */}
      <div className="memory-detail-admin">
        {!showDeleteForm ? (
          <button 
            className="delete-trigger-btn"
            onClick={() => setShowDeleteForm(true)}
            title="Delete memory"
          >
            ⋯
          </button>
        ) : (
          <div className="delete-form">
            <form onSubmit={handleDelete}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="delete-password-input"
                autoFocus
              />
              <div className="delete-form-buttons">
                <button 
                  type="submit" 
                  disabled={deleting}
                  className="delete-confirm-btn"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowDeleteForm(false);
                    setPassword('');
                    setError('');
                  }}
                  className="delete-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
            {error && <div className="delete-error">{error}</div>}
          </div>
        )}
      </div>

      <div className="memory-detail-back">
        <button onClick={() => navigate('/')} className="back-btn">
          ← Back to Memories
        </button>
      </div>
    </div>
  );
}

export default MemoryDetail;
